/* eslint-disable max-lines */
import {
  ColDef,
  ColGroupDef,
  ICellEditorParams,
  ICellRendererParams,
  IRichCellEditorParams,
  ValueFormatterParams,
} from '@ag-grid-community/core';
import { ValueSetterParams } from '@ag-grid-community/core/dist/esm/es6/entities/colDef';
import dayjs from 'dayjs';
import { isNumber } from 'lodash-es';

import { VariationStatusComponent } from '@features/variation-status/variation-status.component';

import {
  AgCustomHeader,
  AgCustomHeaderParams,
} from '@shared/ag-components/ag-custom-header/ag-custom-header';
import {
  AgSelectEditorOptionRendererComponent,
  AgSelectEditorOptionRendererParams,
} from '@shared/ag-components/ag-select-editor-option-renderer/ag-select-editor-option-renderer.component';
import { AgTrendIndicatorComponent } from '@shared/ag-components/ag-trend-indicator/ag-trend-indicator.component';
import { EDITABLE_CELL, EDITABLE_HEADER } from '@shared/constants/ag-classes';
import { MessagesConstants } from '@shared/constants/messages.constants';
import { ActivityType } from '@shared/services/gql.service';
import { getCellClassForGroups } from '@shared/utils/ag-cell-class-for-groups';
import {
  decimalDivide,
  decimalMultiply,
  decimalRoundingToNumber,
  isRoundedToZero,
} from '@shared/utils/floating-math';
import { Utils } from '@shared/utils/utils';

import { AgAdjustmentDiscountTooltipComponent } from '../ag-adjustment-discount-tooltip.component';
import { ExpenseSource } from '../quarter-close-adjustments.model';
import { ExpenseSourceLink } from './expense-source-link';
import {
  currencyFormatter,
  defaultHeaderClass,
  defaultMinWidth,
  defaultWidthForMoneyColumns,
  InMonthAdjustmentGridConfigContext,
  InvoiceExpenseSourceFilterType,
  isDiscountRow,
  isTotalRow,
} from './ima-grid-config-context';
import {
  AgAdjustmentColumnComponent,
  AgAdjustmentColumnParams,
} from '../ag-adjustment-column.component';
import { ImaAggTableService } from '../services/ima-agg-table.service';

export const imaTotalExpenseColumnGroup = (
  ctx: InMonthAdjustmentGridConfigContext
): ColGroupDef => {
  return {
    children: [
      getMonthlyPercentComplete(),
      getMonthlyUnitsColumn(ctx),
      getMonthlyExpenseColumn(ctx),
      getVarianceToForecastColumn(),
      getExpenseSource(ctx),
      getHistoricalAdjustmentAmountColumn(ctx),
      getTotalAdjustmentColumn(ctx),
      getMonthlyExpenseHistoricalAdjustmentColumn(),
      ...getVarianceToPreviousMonthColumns(),
      getExpenceLtd(ctx),
      getNotes(ctx),
      getSupport(ctx),
    ],
    headerClass: ['text-center', 'header-marker', 'green-light-marker'],
    headerName: 'Total Expense',
  };
};

const getMonthlyPercentComplete = (): ColDef => {
  return {
    aggFunc: ImaAggTableService.aggregateTotalMonthlyCompletePercent,
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    comparator: (a, b) => {
      const aVal = a?.tma_percentage || 0;
      const bVal = b?.tma_percentage || 0;

      return aVal === bVal ? 0 : aVal > bVal ? 1 : -1;
    },
    field: 'tma_percentage',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: 'Monthly % Complete',
    headerTooltip: 'Monthly % Complete',
    minWidth: defaultMinWidth,
    valueFormatter: ({ value }) =>
      Utils.percentageFormatter((value?.tma_percentage || 0) / 100, {
        maximumFractionDigits: 4,
      }),
    valueGetter: (params) => {
      const { total_amount = 0, total_monthly_expense = 0 } = params.node?.data || {};

      const tma_percentage = total_amount
        ? decimalMultiply(decimalDivide(total_monthly_expense, total_amount), 100, 6)
        : 0;

      return {
        tma_percentage,
        toString: () => tma_percentage.toString(),
        total_amount,
        total_monthly_expense,
      };
    },
    width: 135,
  };
};

const getMonthlyUnitsColumn = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { editMode$ } = ctx.qcaComponent;
  const canEditOpenMonth = ctx.permissions.modifyOpenMonthAdjustments;
  const isEditable = canEditOpenMonth;

  return {
    cellClass: (params) => {
      const classes = ['text-right', ...getCellClassForGroups(params)];

      if (isEditable && editMode$.value && !isTotalRow(params) && !isDiscountRow(params)) {
        classes.push(EDITABLE_CELL);
      }

      return classes;
    },
    cellRenderer: AgAdjustmentDiscountTooltipComponent,
    cellRendererParams: {
      hasPermission: canEditOpenMonth,
      isInEditMode: editMode$,
    },
    editable: (params) => {
      if (isTotalRow(params)) {
        return false;
      }

      if (isDiscountRow(params)) {
        return false;
      }

      return isEditable && editMode$.value;
    },
    field: 'tma_unit',
    filter: 'agNumberColumnFilter',
    headerClass: () => {
      const classes = [...defaultHeaderClass];

      if (isEditable && editMode$.value) {
        classes.push(EDITABLE_HEADER);
      }

      return classes;
    },
    headerName: 'Monthly Units',
    headerTooltip: canEditOpenMonth
      ? 'Monthly Units'
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
    minWidth: defaultMinWidth,
    valueFormatter: ({ value }) => Utils.decimalFormatter(value),
    valueParser: (params) => {
      return params.newValue === params.oldValue
        ? params.oldValue
        : Utils.castStringToNumber(params.newValue).toString();
    },
    width: 115,
    // aggFunc: this.showUnitTotals$.getValue() ? 'sum' : '',
  };
};

const getMonthlyExpenseColumn = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { editMode$, selected_month } = ctx.qcaComponent;
  const totalDate = dayjs(selected_month.value).format('MMM YYYY');
  const canEditOpenMonth = ctx.permissions.modifyOpenMonthAdjustments;
  const isEditable = canEditOpenMonth;
  const headerName = `${totalDate} Monthly Expense ($)`;

  return {
    cellClass: (params) => {
      const classes = ['text-right', ...getCellClassForGroups(params)];

      if (isEditable && editMode$.value && !isTotalRow(params) && !isDiscountRow(params)) {
        classes.push(EDITABLE_CELL);
      }

      return classes;
    },
    cellRenderer: AgAdjustmentDiscountTooltipComponent,
    cellRendererParams: {
      hasPermission: canEditOpenMonth,
      isInEditMode: editMode$,
    },
    editable: (params) => {
      if (isTotalRow(params)) {
        return false;
      }

      if (isDiscountRow(params)) {
        return false;
      }

      return isEditable && editMode$.value;
    },

    field: 'tma_amount',
    filter: 'agNumberColumnFilter',
    headerClass: () => {
      const classes = [...defaultHeaderClass];

      if (isEditable && editMode$.value) {
        classes.push(EDITABLE_HEADER);
      }

      return classes;
    },
    headerName,
    headerTooltip: canEditOpenMonth
      ? headerName
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    valueParser: (params) => {
      return params.newValue === params.oldValue
        ? params.oldValue
        : Utils.castStringToNumber(params.newValue).toString();
    },
    width: 180,
    // aggFunc: 'sum',
  };
};

const getVarianceToForecastColumn = (): ColDef => {
  return {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    cellRenderer: VariationStatusComponent,
    field: 'variance_to_forecast',
    // aggFunc: 'sum',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: 'Variance to Forecast',
    headerTooltip: 'Variance to Forecast',
    minWidth: defaultMinWidth,
    valueFormatter: (params) => {
      return currencyFormatter({
        ...params,
        value: decimalRoundingToNumber(params.value),
      });
    },
    valueParser: (params) => Number(params.newValue),
    width: 160,
  };
};

const getTmaCellEditorParams = (
  ctx: InMonthAdjustmentGridConfigContext,
  params: ICellEditorParams | ValueSetterParams
): IRichCellEditorParams => {
  const {
    costCategoryHasForecastSelected,
    invoiceExpenseSourceFilter,
    vendorEstimateSelectableCategories,
  } = ctx.qcaComponent;

  const isVendorEstimateAvailable =
    !!params.data.vendor_estimate ||
    vendorEstimateSelectableCategories.has(
      `${params.data.activity_type as ActivityType}_${params.data.organization_id}`
    );
  const shouldShowEB =
    params.data.activity_type === ActivityType.ACTIVITY_INVESTIGATOR &&
    params.data.evidence_based_exist;
  const shouldShowManual = ctx.rows.sourceChanged.has(params.data.activity_id)
    ? false
    : !!params.data.manual_adjustment ||
      ctx.rows.unitChanged.has(params.data.activity_id) ||
      ctx.rows.totalChanged.has(params.data.activity_id);
  const shouldShowForecast = !!params.data.is_forecasted || costCategoryHasForecastSelected(params);
  const shouldShowInvoice =
    invoiceExpenseSourceFilter()[
      `${params.data.activity_type}_${params.data.organization_id}` as keyof InvoiceExpenseSourceFilterType
    ];

  return <IRichCellEditorParams>{
    cellHeight: 24,
    cellRenderer: AgSelectEditorOptionRendererComponent,
    cellRendererParams: <AgSelectEditorOptionRendererParams>{
      pendoId: (params: ICellRendererParams) => {
        switch (params.value) {
          case ExpenseSource.Forecast:
            return 'expense-source-forecast';
          case ExpenseSource.Manual:
            return 'expense-source-manual';
          case ExpenseSource['Evidence Based']:
            return 'expense-source-evidence-based';
          case ExpenseSource['Vendor Estimate']:
            return 'expense-source-vendor-estimate';
          default:
            return '';
        }
      },
    },
    values: [
      shouldShowForecast ? ExpenseSource.Forecast : null,
      isVendorEstimateAvailable ? ExpenseSource['Vendor Estimate'] : null,
      shouldShowEB ? ExpenseSource['Evidence Based'] : null,
      shouldShowInvoice ? ExpenseSource.Invoice : null,
      shouldShowManual ? ExpenseSource.Manual : null,
    ].filter((x) => x),
  };
};

const getExpenseSource = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { editMode$ } = ctx.qcaComponent;
  const canEditOpenMonth = ctx.permissions.modifyOpenMonthAdjustments;
  const isEditable = canEditOpenMonth;

  return {
    cellClass: (params) => {
      const classes = [...getCellClassForGroups(params)];

      if (isEditable && editMode$.value && !isTotalRow(params) && !isDiscountRow(params)) {
        classes.push(EDITABLE_CELL);
      }

      return classes;
    },
    cellEditor: 'agRichSelectCellEditor',
    cellEditorParams: (params: ICellEditorParams) => getTmaCellEditorParams(ctx, params),
    cellRenderer: AgAdjustmentDiscountTooltipComponent,
    cellRendererParams: {
      hasPermission: canEditOpenMonth,
      isInEditMode: editMode$,
    },
    editable: (params) => {
      if (isTotalRow(params)) {
        return false;
      }

      if (isDiscountRow(params)) {
        return false;
      }

      return isEditable && editMode$.value;
    },
    field: 'tma_source',
    headerClass: () => {
      const classes = [...defaultHeaderClass];

      if (isEditable && editMode$.value) {
        classes.push(EDITABLE_HEADER);
      }

      return classes;
    },
    headerComponent: AgCustomHeader,
    headerComponentParams: <AgCustomHeaderParams>{
      innerComponent: ExpenseSourceLink,
      innerComponentInputs: {
        class: 'ag-header-cell-text',
        label: 'Expense Source',
      },
    },
    headerName: 'Expense Source',
    headerTooltip: canEditOpenMonth
      ? 'Expense Source'
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
    minWidth: defaultMinWidth,
    valueFormatter: (params: ValueFormatterParams) => {
      if (!params.value) {
        return Utils.zeroHyphen;
      }

      return params.value;
    },
    valueSetter: (params) => {
      // eslint-disable-next-line @typescript-eslint/ban-ts-comment
      // @ts-ignore
      const options: string[] = getTmaCellEditorParams(ctx, params).values || [];

      if (params.newValue && options.includes(params.newValue)) {
        params.data.tma_source = params.newValue;

        return true;
      }

      return false;
    },
    width: 140,
  };
};

const getHistoricalAdjustmentAmountColumn = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { editMode$ } = ctx.qcaComponent;
  const canEditOpenMonth = ctx.permissions.modifyOpenMonthAdjustments;
  const isEditable = canEditOpenMonth;

  return {
    cellClass: (params) => {
      const classes = ['text-right', ...getCellClassForGroups(params)];

      if (isEditable && editMode$.value && !isTotalRow(params) && !isDiscountRow(params)) {
        classes.push(EDITABLE_CELL);
      }

      return classes;
    },
    cellRenderer: AgAdjustmentDiscountTooltipComponent,
    cellRendererParams: {
      hasPermission: canEditOpenMonth,
      isInEditMode: editMode$,
    },
    editable: (params) => {
      if (isTotalRow(params)) {
        return false;
      }

      if (isDiscountRow(params)) {
        return false;
      }

      return isEditable && editMode$.value;
    },
    field: 'historical_adjustment_amount',
    filter: 'agNumberColumnFilter',
    headerClass: () => {
      const classes = [...defaultHeaderClass];

      if (isEditable && editMode$.value) {
        classes.push(EDITABLE_HEADER);
      }

      return classes;
    },
    headerName: 'Historical Adjustment',
    headerTooltip: canEditOpenMonth
      ? 'Historical Adjustment'
      : MessagesConstants.DO_NOT_HAVE_PERMISSIONS_TO_ACTION,
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    valueParser: (params) => {
      return params.newValue === params.oldValue
        ? params.oldValue
        : Utils.castStringToNumber(params.newValue).toString();
    },
    width: defaultWidthForMoneyColumns,
    // aggFunc: 'sum',
  };
};

const getTotalAdjustmentColumn = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { selected_month, users } = ctx.qcaComponent;

  return {
    cellClass: (params) => ['text-right', 'relative', ...getCellClassForGroups(params)],
    cellRenderer: AgAdjustmentColumnComponent,

    cellRendererParams: <AgAdjustmentColumnParams>{
      adjustmentDate: selected_month?.value || '',
      users: users,
    },
    field: 'total_adjustment',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: 'Total Adjustment',
    headerTooltip: 'Total Adjustment',
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    valueParser: (params) => Number(params.newValue),
    width: 155,
    // aggFunc: 'sum',
  };
};

const getMonthlyExpenseHistoricalAdjustmentColumn = (): ColDef => {
  return {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    field: 'total_monthly_expense',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: 'Monthly Expense incl. Historical Adjustments ($)',
    headerTooltip: 'Monthly Expense incl. Historical Adjustments ($)',
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    width: 240,
    // aggFunc: 'sum',
  };
};

const getVarianceToPreviousMonthColumns = (): ColDef[] => {
  return [
    {
      cellClass: (params) => ['text-right', '!py-0', ...getCellClassForGroups(params)],
      cellRenderer: AgTrendIndicatorComponent,
      cellRendererParams: (params: ICellRendererParams) => ({
        isIconHidden: !!params.value && isRoundedToZero(params.value),
        oneColorIcons: true,
        textColorClass: params.value < 0 ? 'text-aux-red-dark' : '',
      }),
      field: 'variance_to_previous_month',
      filter: 'agNumberColumnFilter',
      headerClass: ['text-center', 'whitespace-normal'],
      headerName: 'Variance to Previous Month ($)',
      minWidth: 200,
      valueFormatter: (params: ValueFormatterParams) => {
        return Utils.currencyFormatter(
          isNumber(params.value) && isRoundedToZero(params.value) ? 0 : params.value,
          {},
          params.data.vendorCurrency
        );
      },
    },
    {
      cellClass: (params) => ['text-right', '!py-0', ...getCellClassForGroups(params)],
      cellRenderer: AgTrendIndicatorComponent,
      cellRendererParams: (params: ICellRendererParams) => ({
        isIconHidden: !!params.value && isRoundedToZero(params.value),
        oneColorIcons: true,
        textColorClass: params.value < 0 ? 'text-aux-red-dark' : '',
      }),
      field: 'variance_to_previous_month_percentage',
      filter: 'agNumberColumnFilter',
      headerClass: ['text-center', 'whitespace-normal'],
      headerName: 'Variance to Previous Month (%)',
      minWidth: 200,
      valueFormatter: (params: ValueFormatterParams) => {
        return Utils.percentageFormatter(isRoundedToZero(params.value, 3) ? 0 : params.value, {
          maximumFractionDigits: 2,
        });
      },
    },
  ];
};

const getExpenceLtd = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { selected_month } = ctx.qcaComponent;
  const totalDate = dayjs(selected_month.value).format('MMM YYYY');

  return {
    cellClass: (params) => ['text-right', ...getCellClassForGroups(params)],
    field: 'expense_ltd',
    filter: 'agNumberColumnFilter',
    headerClass: defaultHeaderClass,
    headerName: `Expense LTD incl. ${totalDate}`,
    headerTooltip: `Expense LTD incl. ${totalDate}`,
    minWidth: defaultMinWidth,
    valueFormatter: currencyFormatter,
    width: 155,
    // aggFunc: 'sum',
  };
};

const getNotes = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { openNoteDialog } = ctx.qcaComponent;

  return {
    cellClass: (params) => ['text-aux-blue-light-200', ...getCellClassForGroups(params)],
    comparator: (_, __, nodeA, nodeB) => {
      const aCount = nodeA.data?.notes?.length || 0;
      const bCount = nodeB.data?.notes?.length || 0;

      return aCount === bCount ? 0 : aCount > bCount ? 1 : -1;
    },
    field: 'notes',
    filter: 'agNumberColumnFilter',
    filterParams: {
      comparator: (a: string, b: string) => Number(a) - Number(b),
      valueFormatter: (params: ValueFormatterParams) => {
        const count = Number(params.value);

        return count === 0 ? '(Blanks)' : `${count} Note${count > 1 ? 's' : ''}`;
      },
    },
    headerClass: defaultHeaderClass,
    headerName: 'Notes',
    headerTooltip: 'Notes',
    minWidth: defaultMinWidth,
    onCellClicked: (event) => !isTotalRow(event) && void openNoteDialog(event),
    valueFormatter: (params) => {
      if (params.data && !params.node?.isRowPinned()) {
        const { length } = params.data.notes || [];

        return length ? `${length} Note${length > 1 ? 's' : ''}` : 'Add Note';
      }

      return '';
    },
    valueGetter: (params) => {
      return params.data?.notes?.length || 0;
    },
    width: 110,
  };
};

const getSupport = (ctx: InMonthAdjustmentGridConfigContext): ColDef => {
  const { openSupportDocumentsDialog } = ctx.qcaComponent;

  return {
    cellClass: (params) => ['text-aux-blue-light-200', ...getCellClassForGroups(params)],
    comparator: (_, __, nodeA, nodeB) => {
      const aCount = nodeA.data.total_documents || 0;
      const bCount = nodeB.data.total_documents || 0;

      return aCount === bCount ? 0 : aCount > bCount ? 1 : -1;
    },
    field: 'total_documents',
    filterParams: {
      comparator: (a: string, b: string) => Number(a) - Number(b),
      valueFormatter: (params: ValueFormatterParams) => {
        const count = Number(params.value);

        return count === 0 ? '(Blanks)' : `${count} Document${count > 1 ? 's' : ''}`;
      },
    },
    headerClass: defaultHeaderClass,
    headerName: 'Support',
    headerTooltip: 'Support',
    minWidth: defaultMinWidth,
    onCellClicked: (event) => !isTotalRow(event) && void openSupportDocumentsDialog(event),
    valueFormatter: (params) => {
      if (params.data && !params.node?.isRowPinned()) {
        const documentCount = params.data.total_documents || 0;

        return documentCount
          ? `${documentCount} Document${documentCount > 1 ? 's' : ''}`
          : 'Add Document';
      }

      return '';
    },
    width: 130,
  };
};
